/**
 * Nome, trabalhador e histórico de audiometrias para os cartões da aba de exames (#461).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import appointmentService from '../../../services/appointmentService';
import audiometryService from '../../../services/audiometryService';
import type { AudiometryHistory } from '../../../types/audiometry';

export interface ContextoDoTrabalhador {
  employeeId: number | null;
  name: string | null;
  /** Data do atendimento: é a data do exame na comparação. `null` quando não veio. */
  appointmentDate: string | null;
  history: AudiometryHistory | null;
}

const CONTEXTO_VAZIO: ContextoDoTrabalhador = { employeeId: null, name: null, appointmentDate: null, history: null };

/**
 * Lê o atendimento e o histórico do trabalhador uma vez por aba — cada leitura do histórico grava
 * um registro de auditoria, então não pode ser repetida por cartão. `ativo` é falso quando a aba
 * não tem nenhuma audiometria editável, e nada é lido. O atendimento só é pedido a quem pode
 * lê-lo (`appointment.read`, mais do que a porta da consulta cobra); sem isso o cartão segue com
 * o que o exame já traz.
 */
export function useContextoDoTrabalhador(appointmentId: number, ativo: boolean) {
  const carregarAtendimento = useCallback(() => appointmentService.getAppointmentById(appointmentId), [appointmentId]);
  const atendimento = useDadoPermitido('appointment.read', 'Dados do atendimento', carregarAtendimento, {
    habilitado: ativo,
  });
  const [history, setHistory] = useState<AudiometryHistory | null>(null);

  const { dado } = atendimento;
  const employeeId = dado ? Number(dado.employeeId) : null;

  const refreshHistory = useCallback(async (id: number) => {
    try {
      setHistory(await audiometryService.getHistory(id));
    } catch {
      // Sem o histórico a tela segue sem o diálogo de troca; o servidor continua sendo o árbitro.
    }
  }, []);

  useEffect(() => {
    setHistory(null);
    if (employeeId === null) return undefined;
    let cancelled = false;
    audiometryService
      .getHistory(employeeId)
      .then((lido) => {
        if (!cancelled) setHistory(lido);
      })
      .catch(() => {
        // Contexto é conforto: sem ele o cartão usa só o que o exame já traz.
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const context = useMemo<ContextoDoTrabalhador>(
    () =>
      dado && employeeId !== null
        ? { employeeId, name: dado.employee?.name ?? null, appointmentDate: dado.selectedDate ?? null, history }
        : CONTEXTO_VAZIO,
    [dado, employeeId, history],
  );

  return { context, refreshHistory };
}
